import Image from '@src/quince-site-common/src/domains/Image'

import isBrowser from '../../utils/isBrowser'
import { AddOnProductOptionGroup } from '../AddOnProductOptionGroup'
import { ContentfulSizeChart } from '../contentful/ContentfulProduct'
import CustomSection from '../CustomSection'
import { Gender } from '../Gender'
import { MergedSizeChart } from '../MergedSizeChart'
import { ProductClassification } from '../productClassification'
import ProductComparisonBrand from '../ProductComparisonBrand'
import { ProductCustomField } from '../ProductCustomField'
import ProductHierarchy from '../ProductHierarchy'
import ProductImage from '../ProductImage'
import { ProductOption } from '../ProductOption'
import ProductReview, { ProductReviewSummary } from '../ProductReview'
import ProductTag from '../ProductTag'
import ProductTransparentPricing from '../ProductTransparentPricing'
import ProductVideo from '../ProductVideo'
import RichText from '../RichText'

import Product from './Product'
import { ProductRecommendation } from './ProductRecommendation'

import type { Document } from '@contentful/rich-text-types'

export enum PRODUCT_TYPES {
  NORMAL = 'Normal',
  BUNDLE = 'Bundle',
  GROUP = 'Group',
}

export interface IBundle {
  readonly id: string
  readonly title: string
  readonly slug: string
  readonly productOptions: ProductOption[]
  readonly images: ProductImage[]
  readonly tags: ProductTag[]
  readonly spid: string
  readonly childSpids?: number[]
  readonly comparisonBrands?: ProductComparisonBrand[] | null
  readonly showAllColors?: boolean | null
  readonly contentfulProductId: string
  readonly gender?: Gender | null
  readonly videos: ProductVideo[]
  readonly description?: RichText | string | undefined | null
  readonly details?: RichText | undefined | null
  readonly disclaimer?: string | undefined | null
  readonly sizeAndFit?: RichText | undefined | null
  readonly productTypeName: string | null
  readonly careAndMaintenance?: RichText | undefined | null
  readonly customFields?: any[] | undefined | null
  readonly productReviewSummary?: ProductReviewSummary | undefined | null
  readonly productReviews?: ProductReview[] | undefined | null
  readonly desktopSections?: CustomSection[] | undefined
  readonly mobileSections?: CustomSection[] | undefined
  readonly transparentPricing?: ProductTransparentPricing | undefined | null
  readonly spHandle?: string | undefined | null
  readonly isMadeToOrder?: boolean
  readonly madeToOrderText?: Document | undefined | null
  readonly sizeChart?: ContentfulSizeChart | undefined | null
  readonly mergedSizeChart?: MergedSizeChart[] | undefined
  readonly vendorCodes: string[] | null
  readonly hideBrandComparison?: boolean | null
  readonly hasRestockingFee?: boolean | null
  readonly hierarchy?: ProductHierarchy[] | undefined | null
  readonly addOnProductOptionGroups?: AddOnProductOptionGroup[]
  readonly customBundleProducts?: Product[]
  readonly addOnBundleProducts?: Product[]
  readonly productDetailIcons?: Image[] | null
  readonly discountPrice?: number | null
  readonly type: PRODUCT_TYPES
  readonly hideSavings?: boolean
  readonly productRecommendations: ProductRecommendation
}

export default class Bundle implements IBundle {
  // eslint-disable-next-line i18next/no-literal-string
  readonly typename = 'Product'
  readonly isConstructedOnServer: boolean
  readonly id: string
  readonly title: string
  readonly slug: string
  readonly productOptions: ProductOption[]
  readonly images: ProductImage[]
  readonly tags: ProductTag[]
  readonly spid: string
  readonly childSpids?: number[]
  readonly showAllColors?: boolean | null
  readonly comparisonBrands?: ProductComparisonBrand[] | null
  readonly contentfulProductId: string
  readonly desktopSections: CustomSection[]
  readonly mobileSections: CustomSection[]
  readonly gender?: Gender | null
  readonly videos: ProductVideo[]
  readonly description: RichText | string | undefined | null
  readonly details: RichText | undefined | null
  readonly disclaimer?: string | undefined | null
  readonly sizeAndFit: RichText | undefined | null
  readonly productTypeName: string | null
  readonly careAndMaintenance: RichText | undefined | null
  readonly productReviewSummary: ProductReviewSummary | undefined | null
  readonly customFields: ProductCustomField[] | undefined | null
  readonly productReviews: ProductReview[] | null
  readonly transparentPricing: ProductTransparentPricing | undefined | null
  readonly spHandle: string | undefined | null
  readonly isMadeToOrder: boolean
  readonly sizeChart: ContentfulSizeChart | undefined | null
  readonly mergedSizeChart?: MergedSizeChart[] | undefined
  readonly vendorCodes: string[] | null
  readonly hideBrandComparison?: boolean | null
  readonly hasRestockingFee?: boolean | null
  readonly hierarchy?: ProductHierarchy[] | undefined | null
  readonly addOnProductOptionGroups?: AddOnProductOptionGroup[]
  readonly customBundleProducts?: Product[]
  readonly addOnBundleProducts?: Product[]
  readonly classification?: ProductClassification

  // TODO: In future, organise below things better, for now, keeping how it is right now.
  readonly madeToOrderText: Document | undefined | null
  readonly traditionalDiscountText?: RichText
  readonly productDetailIcons?: Image[] | null
  readonly discountPrice?: number | null
  readonly type = PRODUCT_TYPES.BUNDLE
  readonly hideSavings?: boolean
  readonly productRecommendations: ProductRecommendation

  constructor(iProduct: IBundle) {
    const {
      id,
      title,
      slug,
      productOptions,
      images,
      tags,
      spid,
      childSpids,
      showAllColors = false,
      comparisonBrands,
      contentfulProductId,
      gender,
      videos,
      description,
      details,
      disclaimer,
      productTypeName,
      careAndMaintenance,
      productReviewSummary,
      customFields = [],
      productReviews = [],
      desktopSections = [],
      mobileSections = [],
      transparentPricing,
      spHandle,
      isMadeToOrder = false,
      madeToOrderText,
      sizeChart,
      mergedSizeChart,
      vendorCodes,
      hideBrandComparison,
      hasRestockingFee,
      hierarchy,
      addOnProductOptionGroups,
      customBundleProducts,
      addOnBundleProducts,
      productDetailIcons,
      sizeAndFit,
      discountPrice,
      hideSavings,
      productRecommendations,
    } = iProduct
    this.id = id
    this.title = title
    this.slug = slug
    this.productOptions = productOptions
    this.images = images
    this.tags = tags
    this.isConstructedOnServer = !isBrowser()
    this.spid = spid
    this.childSpids = childSpids
    this.showAllColors = showAllColors
    this.comparisonBrands = comparisonBrands
    this.contentfulProductId = contentfulProductId
    this.desktopSections = desktopSections
    this.mobileSections = mobileSections
    this.gender = gender
    this.videos = videos
    this.description = description
    this.details = details
    this.disclaimer = disclaimer
    this.sizeAndFit = sizeAndFit
    this.productTypeName = productTypeName
    this.careAndMaintenance = careAndMaintenance
    this.productReviewSummary = productReviewSummary
    this.customFields = customFields
    this.productReviews = productReviews
    this.transparentPricing = transparentPricing
    this.spHandle = spHandle
    this.isMadeToOrder = isMadeToOrder
    this.madeToOrderText = madeToOrderText
    this.sizeChart = sizeChart
    this.mergedSizeChart = mergedSizeChart
    this.vendorCodes = vendorCodes
    this.hideBrandComparison = hideBrandComparison
    this.hasRestockingFee = hasRestockingFee
    this.hierarchy = hierarchy
    this.addOnProductOptionGroups = addOnProductOptionGroups
    this.customBundleProducts = customBundleProducts
    this.addOnBundleProducts = addOnBundleProducts
    this.productDetailIcons = productDetailIcons
    this.discountPrice = discountPrice
    this.hideSavings = hideSavings
    this.productRecommendations = productRecommendations
    return {
      ...this,
    }
  }
}
